import React, { useEffect, useRef, useState } from "react";
import { useAuth, formatApiError } from "../lib/auth";
import { LockKey, SignIn, At, Key, ShieldCheck, ArrowLeft } from "@phosphor-icons/react";

export default function Login() {
  const { login, verifyOtp, resendOtp, googleSignIn } = useAuth();
  const [step, setStep] = useState("credentials"); // "credentials" | "otp"
  const [email, setEmail] = useState("admin@vakuaihr.com");
  const [password, setPassword] = useState("");
  const [tenantKey, setTenantKey] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [redirectingToPortal, setRedirectingToPortal] = useState(false);
  const [challenge, setChallenge] = useState("");
  const [otpDeliveredTo, setOtpDeliveredTo] = useState("");
  const [code, setCode] = useState("");
  const [resendBusy, setResendBusy] = useState(false);
  const [otpNote, setOtpNote] = useState(null);
  const otpInputRef = useRef(null);
  const handledFragment = useRef(false);

  // Handle the Emergent Google OAuth callback synchronously. The redirect
  // lands at /#session_id=<token>; we swap it for a JWT and drop the fragment.
  useEffect(() => {
    if (handledFragment.current) return;
    const match = window.location.hash?.match(/session_id=([^&]+)/);
    if (!match) return;
    handledFragment.current = true;
    const sid = decodeURIComponent(match[1]);
    // Clear the fragment immediately so a refresh doesn't retry.
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    setGoogleBusy(true);
    googleSignIn(sid).catch((e) => {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 && typeof detail === "object" && detail?.redirect === "/portal") {
        setRedirectingToPortal(true);
        setTimeout(() => { window.location.href = "/portal"; }, 1200);
      } else {
        setErr(formatApiError(detail) || e.message);
      }
    }).finally(() => setGoogleBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  useEffect(() => {
    if (step === "otp" && otpInputRef.current) otpInputRef.current.focus();
  }, [step]);

  const submit = async (e) => {
    e?.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const res = await login(email.trim(), password, tenantKey.trim());
      if (res?.requires2fa) {
        setChallenge(res.challenge);
        setOtpDeliveredTo(res.deliveredTo || email.trim());
        setStep("otp");
        setCode("");
        setOtpNote(`A 6-digit code was emailed to ${res.deliveredTo || email.trim()}.`);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 && typeof detail === "object" && detail?.redirect === "/portal") {
        setRedirectingToPortal(true);
        setErr(null);
        setTimeout(() => { window.location.href = "/portal"; }, 1200);
        return;
      }
      setErr(formatApiError(detail) || e.message);
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e?.preventDefault();
    setErr(null);
    if (!/^\d{6}$/.test(code)) { setErr("Enter the 6-digit code from your email."); return; }
    setBusy(true);
    try {
      await verifyOtp(challenge, code);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const resend = async () => {
    setErr(null); setOtpNote(null); setResendBusy(true);
    try {
      await resendOtp(challenge);
      setOtpNote("A new code was sent. It replaces the previous one.");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setResendBusy(false); }
  };

  const goBack = () => {
    setStep("credentials"); setCode(""); setChallenge(""); setErr(null); setOtpNote(null);
  };

  return (
    <div className="min-h-screen flex" style={{ background: "linear-gradient(135deg, #002FA7 0%, #0F1F60 100%)" }}>
      <div className="flex-1 hidden md:flex flex-col justify-center px-16 text-white">
        <div className="font-display text-5xl font-bold tracking-tight leading-[1.05]">
          Vaku HCM Recruit
        </div>
        <div className="mt-3 text-white/80 max-w-md text-lg">
          Agentic AI for Modern Recruitment — Job Requisition to Complete Pre-Hire Tasks.
        </div>
        <div className="mt-10 text-white/60 text-sm font-mono">
          Vaku HCM · Multi-tenant
        </div>
      </div>
      <div className="w-full md:w-[420px] bg-white p-10 flex flex-col justify-center" data-testid="login-page">
        <div className="md:hidden mb-6">
          <div className="font-display text-2xl font-bold">Vaku HCM Recruit</div>
        </div>

        {step === "credentials" && (
          <div data-testid="login-step-credentials">
            <h1 className="font-display text-2xl font-bold tracking-tight">Sign in</h1>
            <p className="text-sm text-slate-500 mt-1 mb-6">Welcome back. Use your work email + your tenant&apos;s access key.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="label-eyebrow block mb-1">Email</label>
                <div className="relative">
                  <At size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input data-testid="login-email" className="input pl-9" type="email"
                    value={email} onChange={(e) => setEmail(e.target.value)} autoFocus required />
                </div>
              </div>
              <div>
                <label className="label-eyebrow block mb-1">Password</label>
                <div className="relative">
                  <LockKey size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input data-testid="login-password" className="input pl-9" type="password"
                    value={password} onChange={(e) => setPassword(e.target.value)} required />
                </div>
              </div>
              <div>
                <label className="label-eyebrow block mb-1">Tenant Access Key <span className="lowercase font-normal text-slate-400">(not required for admins)</span></label>
                <div className="relative">
                  <Key size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input data-testid="login-tenant-key" className="input pl-9 font-mono uppercase tracking-wider" type="text"
                    placeholder="e.g. A1B2C3D4E5F6G7H8"
                    value={tenantKey} onChange={(e) => setTenantKey(e.target.value.toUpperCase())} />
                </div>
                <div className="text-[11px] text-slate-400 mt-1">Recruiters: ask your tenant admin for the access key. Admins can leave blank to recover.</div>
              </div>
              {redirectingToPortal && (
                <div data-testid="login-redirect-portal" className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-sm p-3">
                  <div className="font-semibold">Redirecting you to the candidate portal…</div>
                  <div className="text-xs text-emerald-700 mt-1">
                    That email is registered as a candidate. You&apos;ll sign in with a magic-link email or Google.
                    <a href="/portal" className="underline ml-1">Go now →</a>
                  </div>
                </div>
              )}
              {err && (
                <div data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm p-2">
                  {err}
                </div>
              )}
              <button data-testid="login-submit" type="submit" className="btn btn-primary w-full justify-center" disabled={busy || googleBusy}>
                <SignIn size={14} weight="fill" /> {busy ? "Signing in…" : "Sign In"}
              </button>
            </form>

            <div className="mt-4 flex items-center gap-2">
              <div className="flex-1 border-t border-slate-200"></div>
              <span className="text-[10px] text-slate-400 uppercase tracking-widest">or</span>
              <div className="flex-1 border-t border-slate-200"></div>
            </div>

            <button
              data-testid="login-google"
              type="button"
              onClick={startGoogle}
              disabled={busy || googleBusy}
              className="mt-3 w-full flex items-center justify-center gap-2 border border-slate-300 rounded-sm bg-white hover:bg-slate-50 text-slate-800 text-sm font-medium py-2.5"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.71 4.09-5.5 4.09-3.31 0-6.01-2.74-6.01-6.13S8.69 5.93 12 5.93c1.88 0 3.14.8 3.86 1.48l2.63-2.53C16.86 3.34 14.67 2.4 12 2.4 6.76 2.4 2.5 6.66 2.5 12s4.26 9.6 9.5 9.6c5.48 0 9.11-3.85 9.11-9.28 0-.62-.07-1.1-.16-1.57H12z"/>
                <path fill="#4285F4" d="M23 12.27c0-.83-.07-1.46-.23-2.12H12v3.85h6.24c-.13 1.02-.83 2.55-2.39 3.57l-.02.15 3.47 2.68.24.02C21.6 18.47 23 15.65 23 12.27z"/>
                <path fill="#34A853" d="M12 23c3.15 0 5.79-1.04 7.72-2.83l-3.69-2.85c-.99.69-2.31 1.17-4.03 1.17-3.07 0-5.68-2.03-6.6-4.83l-.14.01-3.62 2.79-.05.13C3.5 20.31 7.44 23 12 23z"/>
                <path fill="#FBBC05" d="M5.4 13.66A6.88 6.88 0 0 1 5.02 12c0-.58.1-1.14.26-1.66l-.01-.11L1.6 7.44l-.11.05A11.53 11.53 0 0 0 .5 12c0 1.86.44 3.61 1.22 5.17L5.4 13.66z"/>
              </svg>
              {googleBusy ? "Signing in with Google…" : "Sign in with Google"}
            </button>
            <div className="mt-4 pt-4 border-t border-slate-200 text-center text-xs text-slate-500">
              Are you a candidate?{" "}
              <a
                href="/portal"
                className="text-[#002FA7] font-medium hover:underline"
                data-testid="candidate-portal-link"
              >
                Sign in to the candidate portal →
              </a>
            </div>
          </div>
        )}

        {step === "otp" && (
          <div data-testid="login-step-otp">
            <button
              type="button"
              onClick={goBack}
              className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-4"
              data-testid="otp-back"
            >
              <ArrowLeft size={12} /> Back to sign-in
            </button>
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck size={22} weight="fill" className="text-[#002FA7]" />
              <h1 className="font-display text-2xl font-bold tracking-tight">Verify it&apos;s you</h1>
            </div>
            <p className="text-sm text-slate-500 mb-6">
              We emailed a 6-digit code to <strong>{otpDeliveredTo}</strong>. Enter it below to finish signing in.
            </p>
            <form onSubmit={verify} className="space-y-4">
              <div>
                <label className="label-eyebrow block mb-1">Verification code</label>
                <input
                  ref={otpInputRef}
                  data-testid="otp-code"
                  className="input font-mono tracking-[0.5em] text-center text-lg"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  pattern="[0-9]{6}"
                  placeholder="••••••"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  required
                />
                <div className="text-[11px] text-slate-400 mt-1">Expires in 10 minutes. Max 5 attempts.</div>
              </div>
              {otpNote && (
                <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-sm p-2" data-testid="otp-note">
                  {otpNote}
                </div>
              )}
              {err && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm p-2" data-testid="otp-error">
                  {err}
                </div>
              )}
              <button
                data-testid="otp-verify"
                type="submit"
                className="btn btn-primary w-full justify-center"
                disabled={busy || code.length !== 6}
              >
                <SignIn size={14} weight="fill" /> {busy ? "Verifying…" : "Verify & sign in"}
              </button>
              <button
                type="button"
                onClick={resend}
                disabled={resendBusy}
                className="text-xs text-[#002FA7] hover:underline w-full text-center"
                data-testid="otp-resend"
              >
                {resendBusy ? "Sending new code…" : "Didn’t get the code? Resend"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
